// /en/ltr/$segment/for/$nationality — Triple-key EN mirror (LTR × nationality)
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { LTR_SEGMENTS } from "@/data/ltr-segments";
import { VISA_NATIONALITIES } from "@/data/visa-nationalities";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, howToSchema, faqPageSchema } from "@/lib/aeo";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { isIndexable } from "@/data/indexable-whitelist";
import { ContactCTA, FaqAccordion, DirectAnswer, KeyFactsTable } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import { Phone, MessageCircle, ArrowRight, Sparkles, FileText, Wallet, Clock, ShieldCheck, BadgeCheck, Globe2 } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";
const GOV_FEE = 50000;

export const Route = createFileRoute("/en/ltr/$segment/for/$nationality")({
  loader: ({ params }) => {
    const seg = LTR_SEGMENTS.find((x) => x.slug === params.segment);
    const nat = VISA_NATIONALITIES.find((n) => n.slug === params.nationality);
    if (!seg || !nat) throw notFound();
    return { seg, nat };
  },
  head: ({ params }) => {
    const seg = LTR_SEGMENTS.find((x) => x.slug === params.segment);
    const nat = VISA_NATIONALITIES.find((n) => n.slug === params.nationality);
    if (!seg || !nat) return { meta: [{ title: ctaTitle("Not found", "/en/ltr/$segment/for/$nationality", "en")}] };
    const url = `${SITE}/en/ltr/${seg.slug}/for/${nat.slug}`;
    const title = `${seg.titleEn} for ${nat.demonymEn} Citizens — Eligibility, Process & Fees 2026 | NYC`;
    const desc = `Apply for ${seg.titleEn} as a citizen of ${nat.countryEn} — ${seg.qualifyEn}. BOI fee THB 50,000 + NYC service THB ${seg.servicePriceTHB.toLocaleString()}. 10-year visa + Digital Work Permit + tax perks. ${nat.visaExempt ? `${nat.visaExempt}-day visa-exempt entry available` : "Pre-arrival visa required"}.`;
    const faqs = [
      { q: `Can ${nat.demonymEn} citizens apply for ${seg.titleEn}?`, a: `Yes — LTR is open to all nationalities including ${nat.countryEn}. File qualification online at ltr.boi.go.th, then collect the visa stamp at the Thai embassy in ${nat.countryEn} or OSS Chamchuri Square (if already in Thailand).` },
      { q: `Do ${nat.demonymEn} citizens need to enter Thailand first?`, a: nat.visaExempt > 0 ? `Not required — submit qualification online from ${nat.countryEn}, or enter visa-exempt for ${nat.visaExempt} days and file at Chamchuri Square in person.` : `Recommended to file qualification online from ${nat.countryEn} first, then collect the visa stamp at the Thai embassy locally.` },
      { q: `How do I prove income / assets?`, a: `${seg.qualifyEn}. All documents must be in English or MFA-certified Thai translations. NYC pre-checks eligibility and prepares the full package end-to-end.` },
      { q: `What tax benefits does LTR give ${nat.demonymEn} holders?`, a: `${seg.perksEn} — plus Royal Decree 743 exempts foreign-sourced income remitted to Thailand for LTR holders (effective 1 Jan 2024), available to all nationalities including ${nat.countryEn}.` },
      { q: `What is NYC's total price?`, a: `Service THB ${seg.servicePriceTHB.toLocaleString()} + BOI fee THB 50,000 = THB ${(seg.servicePriceTHB + GOV_FEE).toLocaleString()} total.` },
      { q: `How long does it take?`, a: `${seg.processingDays}. BOI qualification review takes ~20 business days, then endorsement letter is issued for visa stamping.` },
    ];
    return {
      meta: [
        ...(isIndexable(url) ? [] : [{ name: "robots", content: "noindex,follow" }]),
        { title: ctaTitle(title, "/en/ltr/$segment/for/$nationality", "en") }, { name: "description", content: ctaDesc(desc, "/en/ltr/$segment/for/$nationality", "en")},
        { name: "keywords", content: `LTR ${nat.demonymEn}, Long Term Resident ${nat.countryEn}, ${seg.titleEn} ${nat.demonymEn}, Thailand 10-year visa ${nat.demonymEn}` },
        { property: "og:title", content: ctaTitle(title, "/en/ltr/$segment/for/$nationality", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/ltr/$segment/for/$nationality", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/ltr/${seg.slug}/for/${nat.slug}`, `/en/ltr/${seg.slug}/for/${nat.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: `${seg.titleEn} for ${nat.demonymEn} Citizens`,
              serviceType: seg.titleEn, description: desc, inLanguage: "en",
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", telephone: "+66832494999", url: SITE },
              areaServed: [{ "@type": "Country", name: "Thailand" }, { "@type": "Country", name: nat.countryEn }],
              offers: { "@type": "Offer", price: String(seg.servicePriceTHB), priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          howToSchema({
            name: `How to apply for ${seg.titleEn} as a ${nat.demonymEn} citizen`,
            description: `${seg.taglineEn} — guide for ${nat.countryEn} passport holders`,
            totalTime: "P30D",
            estimatedCost: { currency: "THB", value: String(seg.servicePriceTHB + GOV_FEE) },
            inLanguage: "en",
            steps: seg.stepsEn,
          }),
          faqPageSchema(faqs),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
                { "@type": "ListItem", position: 2, name: "LTR", item: `${SITE}/en/ltr` },
                { "@type": "ListItem", position: 3, name: seg.titleEn, item: `${SITE}/en/ltr/${seg.slug}` },
                { "@type": "ListItem", position: 4, name: nat.countryEn, item: url },
              ],
            }),
          },
          ...reviewNodes(url, `${url}#service`, 2).map((r) => ({ type: "application/ld+json" as const, children: JSON.stringify(r) })),
        ]),
      ],
    };
  },
  component: EnLtrSegmentNationalityPage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">Not found</h1>
      <Link to="/en/ltr" className="mt-4 inline-block text-amber-300 underline">Back to LTR</Link>
    </main>
  ),
});

function EnLtrSegmentNationalityPage() {
  const { seg, nat } = Route.useLoaderData();
  const otherNats = VISA_NATIONALITIES.filter((n) => n.slug !== nat.slug).slice(0, 12);
  const otherSegs = LTR_SEGMENTS.filter((s) => s.slug !== seg.slug);
  const faqs = [
    { q: `Can ${nat.demonymEn} citizens apply for ${seg.titleEn}?`, a: `Yes — open to all nationalities. File at ltr.boi.go.th, then stamp at the Thai embassy in ${nat.countryEn} or OSS Chamchuri Square.` },
    { q: `Do ${nat.demonymEn} citizens need to enter Thailand first?`, a: nat.visaExempt > 0 ? `Not required — file from abroad, or enter visa-exempt ${nat.visaExempt} days then file in-country.` : `Recommended to file from abroad before travel.` },
    { q: `Document language?`, a: "English, or Thai with MFA certification. NYC handles translation in-house." },
    { q: `Tax benefits?`, a: `${seg.perksEn} + Royal Decree 743 exempts foreign-sourced income remitted to Thailand.` },
    { q: `Total price?`, a: `THB ${seg.servicePriceTHB.toLocaleString()} + THB ${GOV_FEE.toLocaleString()} BOI = THB ${(seg.servicePriceTHB + GOV_FEE).toLocaleString()}.` },
    { q: `Turnaround?`, a: `${seg.processingDays}. BOI qualification review ~20 business days.` },
  ];

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_55%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/en" className="hover:text-gold">Home</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/en/ltr" className="hover:text-gold">LTR</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/en/ltr/$segment" params={{ segment: seg.slug }} className="hover:text-gold">{seg.titleEn}</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{nat.flag} {nat.countryEn}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Sparkles className="h-3.5 w-3.5" /> {seg.icon} LTR · {nat.demonymEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            {seg.titleEn}<br />for <span className="text-gold">{nat.demonymEn}</span> Citizens
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            {seg.taglineEn} — guide for {nat.countryEn} passport holders: {nat.visaExempt ? `enter visa-exempt ${nat.visaExempt} days then file in-country` : "pre-arrival application required"}. BOI fee THB 50,000 + NYC service THB {seg.servicePriceTHB.toLocaleString()}. 10-year visa.
          </p>
          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "Total fee", v: `THB ${(seg.servicePriceTHB + GOV_FEE).toLocaleString()}` },
              { k: "Turnaround", v: seg.processingDays },
              { k: "Visa term", v: "10 years" },
              { k: "Visa Exempt", v: nat.visaExempt ? `${nat.visaExempt} days` : "None" },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md">
              <MessageCircle className="h-4 w-4" /> Chat on LINE
            </a>
            <a href="tel:+66832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md">
              <Phone className="h-4 w-4" /> +66 83 249 4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable · AI Overview ready
          </span>
          NYC helps {nat.countryEn} passport holders file {seg.titleEn} — eligibility check ({seg.qualifyEn}), document prep, MFA-certified translation, and BOI qualification submission via ltr.boi.go.th — completed in {seg.processingDays} from THB {seg.servicePriceTHB.toLocaleString()} (plus BOI fee THB 50,000). Ideal for {seg.whoEn}.
        </blockquote>

        <div className="mt-8 grid gap-6 lg:grid-cols-[1.2fr_1fr]">
          <DirectAnswer entity={`LTR for ${nat.countryEn}`}>
            {seg.quickAnswerEn} For {nat.demonymEn} citizens specifically: {nat.notes}
          </DirectAnswer>
          <KeyFactsTable rows={[
            { k: "Nationality", v: `${nat.flag} ${nat.countryEn}` },
            { k: "LTR segment", v: seg.titleEn },
            { k: "Eligibility", v: seg.qualifyEn },
            { k: "BOI fee", v: `THB ${GOV_FEE.toLocaleString()}` },
            { k: "NYC service", v: `THB ${seg.servicePriceTHB.toLocaleString()}` },
            { k: "Visa term", v: "10 years (5+5)" },
          ]} />
        </div>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Why {nat.demonymEn} citizens choose {seg.titleEn}</h2>
          <p className="mt-4 text-[15px] leading-relaxed text-slate-700">
            {nat.notes} — making {seg.titleEn} the premium long-stay route for {nat.demonymEn} who want 10-year Thai residency with tax perks and a Digital Work Permit. {seg.perksEn}
          </p>
          <p className="mt-4 text-[15px] leading-relaxed text-slate-700">
            What sets NYC apart: a team of Notarial Services Attorneys registered with the Lawyers Council of Thailand, in-house MFA-certified translation, and direct experience with BOI's LTR portal. {nat.demonymEn} clients send documents via LINE/email — no travel needed. We manage every step from qualification review to endorsement letter and visa stamping within {seg.processingDays}.
          </p>
        </section>

        <section className="mt-12 grid gap-5 sm:grid-cols-2">
          {[
            { icon: FileText, title: "Core documents", items: seg.docsEn as string[] },
            { icon: ShieldCheck, title: "Eligibility", items: [seg.qualifyEn, seg.whoEn] as string[] },
            { icon: Clock, title: "Timeline", items: seg.stepsEn.map((s: { name: string; text: string }) => `${s.name} — ${s.text.slice(0, 70)}…`) },
            { icon: Wallet, title: "All-in cost", items: [`NYC service THB ${seg.servicePriceTHB.toLocaleString()}`, `BOI fee THB ${GOV_FEE.toLocaleString()}`, "Health insurance USD 50,000+", "Notary +THB 1,500/doc"] as string[] },
          ].map((b) => (
            <div key={b.title} className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
              <div className="flex items-center gap-2"><b.icon className="h-5 w-5 text-amber-600" /><h3 className="font-display text-lg font-extrabold text-navy-deep">{b.title}</h3></div>
              <ul className="mt-3 space-y-1.5 text-sm text-slate-700">
                {b.items.slice(0, 6).map((it: string, i: number) => <li key={i} className="flex gap-2"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />{it}</li>)}
              </ul>
            </div>
          ))}
        </section>

        <section className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">Package for {nat.countryEn}</div>
          <h2 className="mt-1 font-display text-3xl font-extrabold text-navy-deep sm:text-4xl">From THB {seg.servicePriceTHB.toLocaleString()} — end-to-end</h2>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Eligibility check + {seg.docsEn.length}-item document set</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> English translation + MFA certification</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> BOI qualification submission at ltr.boi.go.th + tracking</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Embassy / Chamchuri OSS visa stamp + Digital Work Permit</li>
          </ul>
          <div className="mt-6"><ContactCTA /></div>
        </section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Frequently asked questions</h2>
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-2 sm:p-3">
            <FaqAccordion items={faqs} />
          </div>
        </section>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Other LTR segments for {nat.demonymEn} citizens</h2>
          <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {otherSegs.map((s) => (
              <Link key={s.slug} to="/en/ltr/$segment/for/$nationality" params={{ segment: s.slug, nationality: nat.slug }} className="group flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm hover:border-gold/50">
                <span>{s.icon}</span>
                <span className="flex-1 font-semibold text-slate-800 group-hover:text-amber-700">{s.titleEn}</span>
                <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-10 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{seg.titleEn} for other nationalities</h2>
          <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {otherNats.map((n) => (
              <Link key={n.slug} to="/en/ltr/$segment/for/$nationality" params={{ segment: seg.slug, nationality: n.slug }} className="group flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm hover:border-gold/50">
                <Globe2 className="h-4 w-4 text-amber-600" />
                <span className="flex-1 font-semibold text-slate-800 group-hover:text-amber-700">{n.flag} {n.countryEn}</span>
                <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
              </Link>
            ))}
          </div>
        </section>
      </div>

      <TrustLayer variant="compact" />
    </main>
  );
}
